import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography, alpha } from "@mui/material";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import BoltOsImmersivePanel from "./BoltOsImmersivePanel";
import { CatalogResponse, FALLBACK_CATALOG } from "../immersive-panel-setup/ImmersivePanelSetupPage";

const AI_API_URL = process.env.REACT_APP_AI_API_URL || "http://127.0.0.1:8000";
// const AI_API_URL = "http://127.0.0.1:8000";

type RuntimeChatMessage = {
  author: string;
  text: string;
  avatar: string;
  timestamp?: number;
};

type RuntimeSessionResponse = {
  session_id: string;
  experience_type: "event" | "stream";
  features: string[];
  answers: Record<string, unknown>;
  chat: RuntimeChatMessage[];
  created_at: number;
  updated_at: number;
};

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{
    minHeight: "100vh",
    width: "100%",
    display: "grid",
    placeItems: "center",
    bgcolor: "radial-gradient(circle at top, #10173A 0%, #090D21 60%, #050714 100%)",
    p: { xs: 2, md: 4 },
  }}>
    {children}
  </Box>
);


const ImmersivePanelLaunchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [catalog, setCatalog] = useState<CatalogResponse>(FALLBACK_CATALOG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<RuntimeSessionResponse | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const sessionKey = searchParams.get("session") || "";
  const featureIds = useMemo(
    () =>
      (searchParams.get("features") || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [searchParams],
  );

  useEffect(() => {
    let mounted = true;
    axios
      .get<CatalogResponse>(`${AI_API_URL}/immersive-setup/catalog`)
      .then((res) => {
        if (mounted) {
          setCatalog(res.data);
          setError(null);
        }
      })
      .catch(() => {
        if (mounted) {
          setCatalog(FALLBACK_CATALOG);
          setError("Using default module list.");
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

useEffect(() => {
  let mounted = true;

  if (!sessionKey) {
    setSessionData(null);
    setSessionError(null);
    setSessionLoading(false);
    // return a cleanup in this branch too
    return () => { mounted = false; };
  }

  setSessionLoading(true);
  axios
    .get<RuntimeSessionResponse>(`${AI_API_URL}/immersive-runtime/session/${sessionKey}`)
    .then((res) => {
      if (mounted) {
        setSessionData(res.data);
        setSessionError(null);
      }
    })
    .catch(() => {
      if (mounted) {
        setSessionData(null);
        setSessionError("Unable to load immersive session. Showing defaults.");
      }
    })
    .finally(() => {
      if (mounted) setSessionLoading(false);
    });

  return () => { mounted = false; };
}, [sessionKey]);


  const availableFeatures = useMemo(() => [...catalog.event, ...catalog.stream], [catalog]);

  const finalFeatureIds = useMemo(() => {
    const runtimeIds = sessionData?.features ?? [];
    const fallbacks = runtimeIds.length ? runtimeIds : featureIds;
    return Array.from(new Set(fallbacks));
  }, [sessionData?.features, featureIds]);

  if (loading || sessionLoading) {
    return (
      <PageShell>
        <Box sx={{ display: "grid", placeItems: "center", gap: 2 }}>
          <CircularProgress sx={{ color: "#4CC9F0" }} />
          <Typography sx={{ color: alpha("#fff", 0.8) }}>Loading panel…</Typography>
        </Box>
      </PageShell>
    );
  }

  if (!finalFeatureIds.length) {
    return (
      <PageShell>
        <Typography sx={{ color: alpha("#fff", 0.8) }}>
          No features specified in the link.
        </Typography>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {error && (
        <Typography sx={{ color: alpha("#fff", 0.6), mb: sessionError ? 1 : 2 }}>{error}</Typography>
      )}
      {sessionError && (
        <Typography sx={{ color: alpha("#fff", 0.6), mb: 2 }}>{sessionError}</Typography>
      )}
      <BoltOsImmersivePanel
        features={availableFeatures}
        activeIds={finalFeatureIds}
        sessionKey={sessionKey}
        initialChat={sessionData?.chat}
      />
    </PageShell>
  );
};

export default ImmersivePanelLaunchPage;

